import { observer, useLocalObservable } from 'mobx-react';
import { Tooltip } from 'react-tooltip';
import { useStore } from '../../store/store';
import { PUNCTUATION } from '../../utils/constants/notation';
import { normalizeKeywordQuery } from '../../utils/keywordQuery';
import { useRef, useCallback, useState, useLayoutEffect } from 'react';

const getPlusIconId = (() => {
  let id = 0;
  return () => `icon-button-${++id}`;
})();

// * Align top measurements should be activated only for one ChipList per row
// * If used all alignTopMeasurements props should be provided based on the layout it is being used on

const ChipsList = observer(
  ({
    list = [],
    visibleCount = 2,
    propertyToMap = '',
    alignTopMeasurementsEnabled = false,
    alignTopMeasurementsMinResolutionWidth = 1200,
    alignTopMeasurementsRowQuerySelector = '',
    alignTopMeasurementsItemsContainersQuerySelector = '',
    listClassName = '',
    // TD-2456 — optional clickable-pill support. When `onChipClick` is provided
    // each chip becomes a toggle button; `activeValues` (a Set of the currently
    // filtered values for this column) drives the "active" style. Omitted by
    // every other ChipsList consumer, so their chips stay plain/non-interactive.
    onChipClick = null,
    activeValues = null
  }) => {
    const { utilsStore } = useStore();
    const listRef = useRef(null);
    const [chipWidths, setChipWidths] = useState([]);
    const resizeTimeoutRef = useRef(null);
    const parentResizeObserverRef = useRef(null);
    const rowMutationObserverRef = useRef(null);
    const previousWindowWidthRef = useRef(window.innerWidth); // Track previous window width

    const state = useLocalObservable(() => ({
      plusId: getPlusIconId(),
      isPlusFocused: false,
      setPlusFocused: (isFocused = false) => {
        state.isPlusFocused = isFocused;
      }
    }));

    const isClickable = typeof onChipClick === 'function';

    // TD-2464 — while the "+N" overflow tooltip is open, freeze the chip order
    // so tapping a pill inside it doesn't reflow the tooltip under the cursor;
    // the row reorders once the tooltip closes (see `orderedList` below).
    const [overflowOpen, setOverflowOpen] = useState(false);
    const frozenOrderRef = useRef(null);

    const actualList = Array.isArray(list) ? list : [];

    // TD-2464 — how a chip's value matches the active filter keywords, mirroring
    // the engine's matchKeywordCloud rule: a "quoted" keyword matches only
    // exactly; a bare keyword matches as a substring. 'exact' → solid highlight,
    // 'partial' → soft highlight, null → not matched.
    const matchLevel = useCallback(
      (value) => {
        if (!isClickable || !activeValues || activeValues.size === 0) return null;
        const v = String(value).toLowerCase();
        let partial = false;
        for (const raw of activeValues) {
          const { needle, quoted } = normalizeKeywordQuery(raw);
          if (!needle) continue;
          if (v === needle) return 'exact';
          if (!quoted && v.includes(needle)) partial = true;
        }
        return partial ? 'partial' : null;
      },
      [isClickable, activeValues]
    );

    // TD-2464 — float matching chips (exact first, then partial) to the front so
    // a match is never hidden behind the "+N" overflow. Stable sort preserves the
    // relative order within each tier; non-filterable lists are left untouched.
    const matchRank = (item) => {
      const level = matchLevel(item[propertyToMap] || item);
      if (level === 'exact') return 0;
      if (level === 'partial') return 1;
      return 2;
    };
    const shouldReorder = isClickable && activeValues && activeValues.size > 0;
    let orderedList;
    if (overflowOpen && frozenOrderRef.current) {
      orderedList = frozenOrderRef.current;
    } else {
      orderedList = shouldReorder
        ? [...actualList].sort((a, b) => matchRank(a) - matchRank(b))
        : actualList;
      frozenOrderRef.current = orderedList;
    }

    const visibleItems = orderedList.slice(0, visibleCount);
    const overflowItems = orderedList.slice(visibleCount);
    const showPlusIcon = actualList.length > visibleCount;
    // TD-1715/TD-1716: Extract property value when propertyToMap is set (for object arrays)
    const tooltipContent = showPlusIcon
      ? overflowItems.map((item) => (propertyToMap ? item[propertyToMap] : item)).join(', ')
      : '';

    // TD-2464 — signature of the currently-visible order; re-measure pinned chip
    // widths when a reorder changes which values occupy the visible slots (see
    // the width-measurement effect below).
    const visibleKey = visibleItems.map((item) => item[propertyToMap] || item).join('');

    const performAlignment = useCallback(() => {
      if (
        !alignTopMeasurementsEnabled ||
        !listRef.current ||
        utilsStore.windowWidth <= alignTopMeasurementsMinResolutionWidth
      ) {
        return;
      }

      try {
        const row = alignTopMeasurementsRowQuerySelector
          ? listRef.current.closest(alignTopMeasurementsRowQuerySelector)
          : null;

        if (!row) return;

        const items = [
          ...row.querySelectorAll(
            alignTopMeasurementsItemsContainersQuerySelector.startsWith('>')
              ? `:scope ${alignTopMeasurementsItemsContainersQuerySelector}`
              : alignTopMeasurementsItemsContainersQuerySelector
          )
        ];

        const chipsColumns = items.filter(
          (item) => item.querySelector('.shared-chips-list') !== null
        );

        if (chipsColumns.length) {
          chipsColumns.forEach((item) => {
            const innerChipsList = [...item.querySelectorAll('.shared-chip')];
            const hasMultipleChildren = innerChipsList.length > 1;
            const height = item.offsetHeight;
            const alignValue = hasMultipleChildren && height > 26 ? 'flex-start' : 'auto';

            if (window.getComputedStyle(item).alignSelf !== alignValue) {
              item.style.alignSelf = alignValue;
            }
          });
        }
      } catch (error) {
        console.error('Alignment measurement failed:', error);
      }
    }, [
      alignTopMeasurementsEnabled,
      alignTopMeasurementsMinResolutionWidth,
      alignTopMeasurementsRowQuerySelector,
      alignTopMeasurementsItemsContainersQuerySelector,
      utilsStore.windowWidth
    ]);

    const setupRowObserver = useCallback(() => {
      if (
        !alignTopMeasurementsEnabled ||
        !listRef.current ||
        utilsStore.windowWidth <= alignTopMeasurementsMinResolutionWidth
      ) {
        return;
      }

      if (rowMutationObserverRef.current) {
        rowMutationObserverRef.current.disconnect();
        rowMutationObserverRef.current = null;
      }

      const row = alignTopMeasurementsRowQuerySelector
        ? listRef.current.closest(alignTopMeasurementsRowQuerySelector)
        : null;

      if (!row) return;

      rowMutationObserverRef.current = new MutationObserver((mutations) => {
        if (!listRef.current || !rowMutationObserverRef.current) return;

        let shouldRealign = false;

        for (const mutation of mutations) {
          if (
            mutation.type === 'childList' &&
            (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
          ) {
            shouldRealign = true;
            break;
          }

          if (
            mutation.type === 'attributes' &&
            ['class', 'style'].includes(mutation.attributeName)
          ) {
            shouldRealign = true;
            break;
          }
        }

        if (shouldRealign) {
          setTimeout(() => {
            if (listRef.current) {
              performAlignment();
            }
          }, 0);
        }
      });

      rowMutationObserverRef.current.observe(row, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }, [
      alignTopMeasurementsEnabled,
      alignTopMeasurementsMinResolutionWidth,
      alignTopMeasurementsRowQuerySelector,
      utilsStore.windowWidth,
      performAlignment
    ]);

    useLayoutEffect(() => {
      performAlignment();
      setupRowObserver();

      return () => {
        if (rowMutationObserverRef.current) {
          rowMutationObserverRef.current.disconnect();
          rowMutationObserverRef.current = null;
        }
      };
    }, [performAlignment, setupRowObserver, actualList.length]);

    const updateChipWidths = useCallback(() => {
      if (!listRef.current) return;

      const rect = listRef.current.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      const widths = [];
      const chips = listRef.current.querySelectorAll('.shared-chip') || [];

      chips.forEach((chip) => {
        const span = chip.querySelector('span');
        if (!span) return;

        const chipStyle = window.getComputedStyle(chip);
        const paddingLeft = parseFloat(chipStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(chipStyle.paddingRight) || 0;

        const spanWidth = span.getBoundingClientRect().width;

        const borderLeft = parseFloat(chipStyle.borderLeftWidth) || 0;
        const borderRight = parseFloat(chipStyle.borderRightWidth) || 0;
        const totalWidth = spanWidth + paddingLeft + paddingRight + borderLeft + borderRight;

        widths.push(totalWidth);
      });

      setChipWidths(widths);
    }, []);

    const handleResize = useCallback(() => {
      setChipWidths([]);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        updateChipWidths();
      }, 0);
    }, [updateChipWidths]);

    const handleWindowResize = useCallback(() => {
      const currentWidth = window.innerWidth;

      if (currentWidth !== previousWindowWidthRef.current) {
        previousWindowWidthRef.current = currentWidth;
        handleResize();
      }
    }, [handleResize]);

    const setupParentWidthObserver = useCallback(() => {
      if (!listRef.current) return;

      if (parentResizeObserverRef.current) {
        parentResizeObserverRef.current.disconnect();
        parentResizeObserverRef.current = null;
      }

      const parentElement = listRef.current.parentElement;
      if (!parentElement) return;

      parentResizeObserverRef.current = new ResizeObserver((entries) => {
        if (!listRef.current || !parentResizeObserverRef.current) return;

        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          if (Math.abs(newWidth - (entry.target.previousWidth || 0)) > 1) {
            entry.target.previousWidth = newWidth;
            handleResize();
          }
        }
      });

      parentResizeObserverRef.current.observe(parentElement);
    }, [handleResize]);

    const cleanupObservers = useCallback(() => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
      if (parentResizeObserverRef.current) {
        parentResizeObserverRef.current.disconnect();
        parentResizeObserverRef.current = null;
      }
      if (rowMutationObserverRef.current) {
        rowMutationObserverRef.current.disconnect();
        rowMutationObserverRef.current = null;
      }
    }, []);

    useLayoutEffect(() => {
      if (!listRef.current) return;

      handleResize();
      setupParentWidthObserver();

      window.addEventListener('resize', handleWindowResize);

      return () => {
        window.removeEventListener('resize', handleWindowResize);
        cleanupObservers();
      };
    }, [
      handleResize,
      setupParentWidthObserver,
      cleanupObservers,
      handleWindowResize,
      actualList.length,
      utilsStore.options
    ]);

    // TD-2464 — re-measure when the visible chip order changes (a reorder floats
    // active pills to the front). Widths are pinned by index, so without a fresh
    // measurement pass a chip could render at the previous slot's width.
    useLayoutEffect(() => {
      if (listRef.current) handleResize();
    }, [visibleKey, handleResize]);

    const renderChip = useCallback(
      (item, idx) => {
        const value = item[propertyToMap] || item;
        // TD-2456 — clickable variant: chip is a filter toggle. `activeValues`
        // membership drives the selected style. Falls back to the plain,
        // non-interactive chip (current behaviour) when no handler is supplied.
        if (isClickable) {
          const level = matchLevel(value);
          const isExact = level === 'exact';
          const isPartial = level === 'partial';
          return (
            <button
              type="button"
              key={idx}
              className={`shared-chip clickable${isExact ? ' active' : ''}${
                isPartial ? ' active-partial' : ''
              }${state.isPlusFocused ? ' highlight' : ''}`}
              style={{ width: chipWidths[idx] ? `${chipWidths[idx]}px` : 'auto' }}
              aria-pressed={isExact}
              title={`Filter by "${value}"`}
              onClick={() => onChipClick(value)}
            >
              <span>{value}</span>
            </button>
          );
        }
        return (
          <div
            key={idx}
            className={`shared-chip ${state.isPlusFocused ? 'highlight' : ''}`}
            style={{
              width: chipWidths[idx] ? `${chipWidths[idx]}px` : 'auto'
            }}
          >
            <span>{value}</span>
          </div>
        );
      },
      [propertyToMap, state.isPlusFocused, chipWidths, isClickable, onChipClick, matchLevel]
    );

    if (!actualList.length) {
      return (
        <div className="shared-chips-list empty" ref={listRef}>
          <div className="shared-chip">{PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      );
    }

    return (
      <div className={`shared-chips-list ${listClassName}`} ref={listRef}>
        {visibleItems.map((item, idx) => renderChip(item, idx))}

        {showPlusIcon && (
          <>
            <div
              className="shared-chip noselect plus"
              id={state.plusId}
              onMouseEnter={() => state.setPlusFocused(true)}
              onMouseLeave={() => state.setPlusFocused(false)}
            >
              +{actualList.length - visibleCount}
            </div>
            <Tooltip
              anchorSelect={`#${state.plusId}`}
              {...(isClickable ? {} : { content: tooltipContent })}
              className="shared-tooltip-list"
              multiline
              variant="dark"
              place="bottom-center"
              opacity={1}
              clickable
              // TD-2464 — track open state so the chip order stays frozen while
              // the overflow tooltip is open, then reorders once it closes.
              afterShow={() => setOverflowOpen(true)}
              afterHide={() => setOverflowOpen(false)}
            >
              {/* TD-2456 — when chips are clickable, the overflow tooltip lists
                  the remaining values as clickable filter toggles too, so every
                  pill (not just the first `visibleCount`) can drive a filter. */}
              {isClickable && (
                <div className="shared-tooltip-chips">
                  {overflowItems.map((item) => {
                    const value = item[propertyToMap] || item;
                    const level = matchLevel(value);
                    const isExact = level === 'exact';
                    const isPartial = level === 'partial';
                    return (
                      <button
                        type="button"
                        key={value}
                        className={`shared-chip clickable in-tooltip${isExact ? ' active' : ''}${
                          isPartial ? ' active-partial' : ''
                        }`}
                        aria-pressed={isExact}
                        onClick={() => onChipClick(value)}
                      >
                        <span>{value}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Tooltip>
          </>
        )}
      </div>
    );
  }
);

export default ChipsList;
